import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../AdminDashboard/css/adminprofile.css';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getWithTokenAPI, postWithoutTokenAPI, putAPI } from '../../utils/Api';
import {
    Box,
    Typography,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Alert,
    CircularProgress,
} from "@mui/material";

// Accept onAdminNameChange and loggedInAdminId as props
function AdminProfilePage({ onAdminNameChange, loggedInAdminId }) {
    const { id: paramAdminId } = useParams();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('profile');
    const [adminData, setAdminData] = useState({
        name: '',
        email: '',
        phoneNumber: '',
        profileImage: ''
    });

    console.log(adminData)
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageError, setImageError] = useState('');

    const [newPassword, setNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);

    const DEFAULT_PROFILE_IMAGE_PLACEHOLDER = "https://placehold.co/150x150/cccccc/ffffff?text=No+Image";


    // --- Fetch Admin Data on Component Mount and when relevant IDs change ---
    useEffect(() => {
        const fetchAdminData = async () => {
            setLoading(true);
            let effectiveAdminId = paramAdminId;
            let currentAdminInfo = {};
            if (!effectiveAdminId) {
                try {
                    const adminInfoString = localStorage.getItem('adminInfo');
                    currentAdminInfo = JSON.parse(adminInfoString || '{}');
                    if (currentAdminInfo && currentAdminInfo._id) {
                        effectiveAdminId = currentAdminInfo._id;
                        console.log("AdminProfilePage: Found admin ID in localStorage:", effectiveAdminId);
                    } else {
                        console.warn("AdminProfilePage: No admin ID in URL params or localStorage 'adminInfo'.");
                        toast.warn("Admin ID not found. Please log in or select an admin from the list.");
                        setLoading(false);
                        return;
                    }
                } catch (e) {
                    console.error("AdminProfilePage: Error parsing adminInfo from localStorage:", e);
                    toast.error("Error retrieving admin details. Please try logging in again.");
                    setLoading(false);
                    return;
                }
            } else {
                try {
                    const adminInfoString = localStorage.getItem('adminInfo');
                    currentAdminInfo = JSON.parse(adminInfoString || '{}');
                } catch (e) {
                    console.error("Error parsing adminInfo from localStorage for comparison:", e);
                }
            }

            if (!effectiveAdminId) {
                toast.error("Unable to determine admin ID for fetching profile.");
                setLoading(false);
                return;
            }

            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Authentication token not found. Please log in.");
                setLoading(false);
                return;
            }

            try {
                console.log(`AdminProfilePage: Fetching data for admin ID: ${effectiveAdminId}`);
                const response = await getWithTokenAPI(token, `/admin/find-particular?_id=${effectiveAdminId}`);

                console.log("AdminProfilePage: API Response for particular admin profile:", response);

                if (response.data && response.statusCode === 200) {
                    setAdminData({
                        name: response.data.name || '',
                        email: response.data.email || '',
                        phoneNumber: response.data.phone_number || '',
                        profileImage: response.data.profile_image || '' // Set the URL for display
                    });
                    setProfileImageFile(null); // Reset file input when new data is fetched
                } else {
                    toast.error(response.message || "Admin data not found or invalid structure in response.");
                    setAdminData({ name: '', email: '', phoneNumber: '', profileImage: '' });
                }

            } catch (error) {
                toast.error(`Failed to fetch admin data for ID ${effectiveAdminId}. Please check console.`);
                console.error("Fetch particular admin error:", error);
                setAdminData({ name: '', email: '', phoneNumber: '', profileImage: '' });
            } finally {
                setLoading(false);
            }
        };

        fetchAdminData();
    }, [paramAdminId]); // Re-fetch data if the ID in the URL changes

    // --- Handlers for input changes ---
    const handleProfileInputChange = (e) => {
        const { id, value } = e.target;
        setAdminData(prevData => ({
            ...prevData,
            [id]: value
        }));
    };

    const handleImageChange = (event) => {
        setImageError('');
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];

            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
            const maxSize = 5 * 1024 * 1024; // 5MB

            if (!allowedTypes.includes(file.type)) {
                setImageError("Only JPEG, PNG, or GIF images are allowed.");
                setProfileImageFile(null); // Clear selected file
                setAdminData(prevData => ({ ...prevData, profileImage: DEFAULT_PROFILE_IMAGE_PLACEHOLDER })); // Show placeholder
                return;
            }

            if (file.size > maxSize) {
                setImageError("Image size cannot exceed 5MB.");
                setProfileImageFile(null); // Clear selected file
                setAdminData(prevData => ({ ...prevData, profileImage: DEFAULT_PROFILE_IMAGE_PLACEHOLDER })); // Show placeholder
                return;
            }

            setProfileImageFile(file); // Store the file object to send to API
            const reader = new FileReader();
            reader.onload = (e) => {
                setAdminData(prevData => ({
                    ...prevData,
                    profileImage: e.target.result // Use base64 for immediate preview
                }));
            };
            reader.readAsDataURL(file);
        } else {
            setProfileImageFile(null);
            // Revert to original profile image or default if no file selected
            setAdminData(prevData => ({ ...prevData, profileImage: prevData.profileImage || DEFAULT_PROFILE_IMAGE_PLACEHOLDER }));
        }
    };

    // const handleImageChange = async (e) => {
    //     const files = Array.from(e.target.files);
    //     if (files.length === 0) return;
    //     const formData = new FormData();
    //     files.forEach(file => {
    //         formData.append('files', file);
    //     });
    //     formData.append('path', 'media/admin/');

    //     try {
    //         const response = await postWithoutTokenAPI('/files/upload', formData);

    //         if (response.error) {
    //             throw new Error(response.error);
    //         } else if (response?.statusCode === 200 && response.data) {
    //             const uploadedUrls = response.data.map(item => item.file_url);
    //             setAdminData(prevData => ({
    //                 ...prevData,
    //                 profileImage: uploadedUrls
    //             }));
    //             toast.success("Image uploaded successfully!");
    //         } else {
    //             toast.error("Failed to upload images: " + (response?.message || "Unknown error"));
    //         }
    //     } catch (error) {
    //         console.error('Error uploading images:', error);
    //         toast.error('Failed to upload images. Please try again.');
    //     } finally {
    //         e.target.value = null;
    //     }
    // };


    // --- Save Profile Changes ---
    const handleProfileSaveChanges = async () => {
        setLoading(true);
        let effectiveAdminId = paramAdminId;
        let currentAdminInfo = {}; // To hold the parsed adminInfo for comparison

        // Get the current logged-in admin's info from localStorage
        try {
            const adminInfoString = localStorage.getItem('adminInfo');
            currentAdminInfo = JSON.parse(adminInfoString || '{}');
            // If paramAdminId is not present, use the ID from localStorage
            if (!effectiveAdminId && currentAdminInfo?._id) {
                effectiveAdminId = currentAdminInfo._id;
            }
        } catch (e) {
            console.error("Error parsing adminInfo from localStorage:", e);
            toast.error("Error retrieving admin details. Please try again.");
            setLoading(false);
            return;
        }

        if (!effectiveAdminId) {
            toast.error("Cannot update profile: Admin ID is missing.");
            setLoading(false);
            return;
        }

        if (imageError) { // Check if there's an image error from validation
            toast.error(imageError);
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Authentication token not found. Please log in.");
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append("_id", effectiveAdminId);
            formData.append("name", adminData.name); // Send the updated name
            formData.append("profile_image", adminData.profileImage);

            const response = await putAPI(token, `/admin/update`, formData, true); // Added true for multipart/form-data

            if (response.statusCode === 200) {
                toast.success(response.message || 'Profile updated successfully!');

                // --- CRITICAL: Check if the updated profile belongs to the currently logged-in admin ---
                // We use loggedInAdminId prop for this which comes from App.js.
                // It's the most reliable way to know if *this* user's name changed.
                if (effectiveAdminId === loggedInAdminId && onAdminNameChange) {
                    console.log("AdminProfilePage: Logged-in admin's name updated. Calling global updater.");
                    onAdminNameChange(adminData.name, effectiveAdminId); // Call the prop function with the new name and ID
                }

                // You might want to refresh the fetched data if the image or other fields were updated by the backend
                // Or simply trust the backend response to update state if it sends back the full new admin object.
                // For now, it just navigates away.
                navigate('/backoffice/adminmanagement'); // Or whatever page makes sense after saving
            } else {
                toast.error(response.message || 'Failed to update profile. Please try again.');
            }
        } catch (error) {
            toast.error('Failed to update profile due to a network error.');
            console.error("Update profile error:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Save Password Changes ---
    const handlePasswordSaveChanges = async () => {
        setLoading(true);
        let effectiveAdminId = paramAdminId;
        if (!effectiveAdminId) {
            try {
                const adminInfo = JSON.parse(localStorage.getItem('adminInfo'));
                effectiveAdminId = adminInfo?._id;
            } catch (e) { /* ignore parse error, will be caught by !effectiveAdminId check */ }
        }

        if (!effectiveAdminId) {
            toast.error("Cannot change password: Admin ID is missing.");
            setLoading(false);
            return;
        }

        if (!newPassword) {
            toast.error('New password is required.');
            setLoading(false);
            return;
        }
        if (newPassword.length < 6) {
            toast.error('New password must be at least 6 characters long.');
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Authentication token not found. Please log in.");
                setLoading(false);
                return;
            }

            const response = await putAPI(token, `/admin/admin-reset-password`, {
                _id: effectiveAdminId,
                password: newPassword
            });

            if (response.statusCode === 200) {
                toast.success(response.message || 'Password updated successfully!');
                setNewPassword(''); // Clear the password field
            } else {
                toast.error(response.message || 'Failed to update password. Please try again.');
            }
        } catch (error) {
            toast.error('Failed to update password due to a network error.');
            console.error("Change password error:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Loading State Display ---
    if (loading) {
        return (
            <div className="admin-profile-container full-width-layout">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress style={{ color: "#00b207" }} />
                    <Typography sx={{ ml: 2 }}>Loading admin data...</Typography>
                </Box>
            </div>
        );
    }

    return (
        <div className="admin-profile-container full-width-layout">
            <h2 className="profile-heading">Account Settings</h2>

            <div className="tabs-container">
                <button
                    className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    Profile
                </button>
                <button
                    className={`tab-button ${activeTab === 'password' ? 'active' : ''}`}
                    onClick={() => setActiveTab('password')}
                >
                    Change Password
                </button>
            </div>

            <div className="tab-content">
                {activeTab === 'profile' && (
                    <div className="profile-tab-section no-card-effect">
                        <div className="profile-form-section">
                            <div className="form-group">
                                <label htmlFor="name">Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    className="form-control"
                                    value={adminData?.name || ''}
                                    onChange={handleProfileInputChange}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    className="form-control"
                                    value={adminData?.email || ''}
                                    disabled // Email is typically not editable from profile
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="phoneNumber">Phone Number</label>
                                <input
                                    type="text"
                                    id="phoneNumber"
                                    className="form-control"
                                    value={adminData?.phoneNumber || ''}
                                    disabled // Phone number typically not editable
                                />
                            </div>
                            <button className="save-changes-button" onClick={handleProfileSaveChanges} disabled={loading}>
                                {loading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
                            </button>
                        </div>
                        <div className="profile-image-section">
                            <div className="profile-image-wrapper">
                                <img
                                    src={adminData?.profileImage || DEFAULT_PROFILE_IMAGE_PLACEHOLDER}
                                    alt="Profile"
                                    className="profile-avatar"
                                    onError={(e) => { e.target.src = DEFAULT_PROFILE_IMAGE_PLACEHOLDER; }}
                                />
                            </div>
                            <input
                                type="file"
                                id="imageUpload"
                                accept="image/jpeg,image/png,image/gif"
                                style={{ display: 'none' }}
                                onChange={handleImageChange}
                            />
                            <label htmlFor="imageUpload" className="choose-image-button">
                                Choose Image
                            </label>
                            {imageError && <Alert severity="error" sx={{ mt: 1, width: '100%' }}>{imageError}</Alert>}
                        </div>
                    </div>
                )}

                {activeTab === 'password' && (
                    <div className="password-tab-section no-card-effect">
                        <div className="form-group password-field">
                            <label htmlFor="newPassword">New Password</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showNewPassword ? 'text' : 'password'}
                                    id="newPassword"
                                    className="form-control"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                                <span
                                    className="password-toggle-icon"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? <FiEyeOff /> : <FiEye />}
                                </span>
                            </div>
                        </div>
                        <button className="save-changes-button" onClick={handlePasswordSaveChanges} disabled={loading}>
                            {loading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
                        </button>
                    </div>
                )}
            </div>
            {/* It's usually better to have ToastContainer in App.js to avoid multiple instances */}
            {/* <ToastContainer /> */}
        </div>
    );
}

export default AdminProfilePage;